import axios from 'axios';
import config from '../config/environment';

const API_BASE_URL = config.API_BASE_URL;

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: {
      id: number;
      username: string;
      role: string;
    };
  };
}

export interface User {
  id: number;
  username: string;
  role: string;
  email?: string;
  created_at?: string;
  updated_at?: string;
}

const authApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Login
export const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  const response = await authApi.post('/api/auth/login', credentials);
  return response.data;
};

// Verify token
export const verifyToken = async (token: string): Promise<{ success: boolean; data: { user: User } }> => {
  const response = await authApi.get('/api/auth/verify', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

// Logout
export const logout = async (): Promise<{ success: boolean; message: string }> => {
  const response = await authApi.post('/api/auth/logout');
  return response.data;
};

// Token management
export const getStoredToken = (): string | null => {
  return localStorage.getItem('authToken');
};

export const setStoredToken = (token: string): void => {
  localStorage.setItem('authToken', token);
};

export const removeStoredToken = (): void => {
  localStorage.removeItem('authToken');
};

export const getStoredUser = (): User | null => {
  const userStr = localStorage.getItem('authUser');
  return userStr ? JSON.parse(userStr) : null;
};

export const setStoredUser = (user: User): void => {
  localStorage.setItem('authUser', JSON.stringify(user));
};

export const removeStoredUser = (): void => {
  localStorage.removeItem('authUser');
};

// Change password
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> => {
  const token = getStoredToken();
  if (!token) throw new Error('No authentication token');

  const response = await authApi.post(
    '/api/auth/change-password',
    { currentPassword, newPassword },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

// Add new user (admin only)
export const addUser = async (
  userData: {
    username: string;
    password: string;
    email: string;
    role: 'admin' | 'user';
  }
): Promise<{ success: boolean; message: string; data: any }> => {
  const token = getStoredToken();
  if (!token) throw new Error('No authentication token');

  const response = await authApi.post(
    '/api/auth/add-user',
    userData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

// Get all users (admin only)
export const getUsers = async (): Promise<{ success: boolean; data: any[] }> => {
  const token = getStoredToken();
  if (!token) throw new Error('No authentication token');

  const response = await authApi.get('/api/auth/users', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};
